import { Fragment, useEffect, useMemo, useState } from 'react'
import { User } from '../../types'
import {
  loadContractsReport,
  loadMonthEndReport,
  recentMonths,
  toCsv,
  downloadCsv,
  ContractsReport,
  MonthEndReport,
} from '../../services/reportsService'
import {
  loadSalesPerformance,
  loadCommissionRecon,
  SalesPerformanceReport,
  CommissionReconReport,
} from '../../services/commissionService'
import SalespersonReport from './SalespersonReport'
import { KpiPeriod, KPI_PERIOD_LABEL } from '../../services/commissionService'
import { ClosingStockJournal } from '../../types'
import { AGE_BUCKET_LABEL } from '../../services/reportsService'
import {
  getJournal,
  postJournal,
  pushJournalToXero,
  reverseJournal,
  reverseDueJournals,
} from '../../services/closingStockService'
import { loadProfitAndLoss, ProfitAndLoss, PlLine } from '../../services/plService'
import ReportRangeFilter from './ReportRangeFilter'
import { ALL_TIME, DateRange, inRange } from '../../lib/dateRange'
import DebtorsCreditorsTab from './DebtorsCreditorsTab'
import PpsrRegisterTab from './PpsrRegisterTab'
import ExpensesTab from './ExpensesTab'
import SalesInvoiceReportTab from './SalesInvoiceReportTab'
import './ReportsView.css'

type Tab = 'salesperson' | 'sales' | 'commission' | 'contracts' | 'monthend' | 'pl' | 'ledger' | 'ppsr' | 'expenses' | 'arinvoices'

/*
 * Winnie, 1 Sep: "All report: should have two decimals instead of rounding amount."
 *
 * Every figure on this screen was rounded to whole dollars for display. On a
 * single line that reads tidier; down a column it stops adding up, because the
 * total is computed from the real numbers and then rounded separately, so a
 * page of rounded rows can miss the rounded total by a dollar or two and the
 * accountant has to go looking for money that was never lost. Cents are the
 * default now. The dp argument stays so a genuinely whole-dollar figure can
 * still ask for one.
 */
const money = (n: number | null | undefined, dp = 2) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`

const fmtDate = (iso?: string | null) =>
  !iso ? '—' : new Date(iso).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })

const isoDate = (iso?: string | null) => (!iso ? '—' : new Date(iso).toISOString().slice(0, 10))

export default function ReportsView({ user: _user }: { user: User }) {
  const [tab, setTab] = useState<Tab>('salesperson')

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'salesperson', label: 'By Salesperson', icon: '🧑‍💼' },
    { id: 'sales', label: 'Team Summary', icon: '📊' },
    { id: 'commission', label: 'Commission Recon', icon: '🧾' },
    { id: 'contracts', label: 'Contracts', icon: '📄' },
    { id: 'monthend', label: 'Month-End', icon: '📅' },
    { id: 'pl', label: 'P&L', icon: '📈' },
    { id: 'ledger', label: 'Debtors & Creditors', icon: '⚖️' },
    // Winnie, 18 Sep: "a new tab under Reports, alongside Debtors & Creditors — this is a
    // compliance/audit report someone checks periodically, not a daily operational screen."
    { id: 'ppsr', label: 'PPSR Register', icon: '🔐' },
    { id: 'arinvoices', label: 'Sales Invoices (AR)', icon: '\uD83E\uDDFE' },
    { id: 'expenses', label: 'Expenses', icon: '🧮' },
  ]

  return (
    <div className="reports">
      <div className="rep-head">
        <div>
          <h2>Reports</h2>
          <p className="rep-sub">Sales performance, commission reconciliation, contract registers, and month-end close.</p>
        </div>
      </div>

      <div className="rep-tabs">
        {tabs.map((t) => (
          <button key={t.id} className={`rep-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <span className="rep-tab-icon">{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {tab === 'salesperson' && <SalespersonReport />}
      {tab === 'contracts' && <ContractsReportTab />}
      {tab === 'monthend' && <MonthEndReportTab />}
      {tab === 'pl' && <ProfitAndLossTab />}
      {tab === 'sales' && <SalesPerformanceTab />}
      {tab === 'commission' && <CommissionReconTab />}
      {tab === 'ledger' && <DebtorsCreditorsTab />}
      {tab === 'ppsr' && <PpsrRegisterTab />}
      {tab === 'arinvoices' && <SalesInvoiceReportTab />}
      {tab === 'expenses' && <ExpensesTab user={_user} />}
    </div>
  )
}

function EmptyModel({ title }: { title: string }) {
  return (
    <div className="rep-empty">
      <div className="rep-empty-icon">📇</div>
      <h3>{title}</h3>
      <p>No team members or attributions yet. Add team members and credit deals to them to populate this report.</p>
    </div>
  )
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rep-stat">
      <div className="rep-stat-label">{label}</div>
      <div className="rep-stat-value">{value}</div>
      {note && <div className="rep-stat-note">{note}</div>}
    </div>
  )
}

// ==========================================================================
// CONTRACTS TAB
// ==========================================================================

function ContractsReportTab() {
  // The register's own date is when the contract was SIGNED, so that is what the
  // range filters — and the control says so, because the same tab also carries an
  // AR register whose rows this window does not touch.
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  return (
    <>
      <ReportRangeFilter value={range} onChange={setRange} label="Contract signed" />
      <ContractsReportBody key={`${range.from}|${range.to}`} range={range} />
    </>
  )
}

function ContractsReportBody({ range }: { range: DateRange }) {
  const [rep, setRep] = useState<ContractsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadContractsReport().then((r) => {
        // Filtering the table but not the tiles above it would put "154 signed"
        // over a list of five. The two counts that are about signing move with
        // the window; total contracts stays a count of live deals, which is what
        // its label says it is.
        const bosRegister = r.bosRegister.filter((row) => inRange(row.signedAt, range))
        return {
          ...r,
          bosRegister,
          stats: {
            ...r.stats,
            signed: bosRegister.length,
            ppsrLodged: bosRegister.filter((row) => !!row.ppsrRef).length,
          },
        }
      })
      .then((r) => !cancelled && setRep(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const exportBos = () => {
    if (!rep) return
    const csv = toCsv(
      ['Deal ID', 'Vehicle', 'VIN', 'Buyer', 'Election Path', 'Signed', 'PPSR Ref', 'Status'],
      rep.bosRegister.map((r) => [
        r.dealId, r.vehicle, r.vin, r.buyer, r.electionLabel, isoDate(r.signedAt), r.ppsrRef, r.complete ? 'Complete' : 'Pending',
      ])
    )
    downloadCsv('bill-of-sale-register.csv', csv)
  }

  if (loading) return <div className="rep-loading">Loading contracts…</div>
  if (error) return <div className="rep-error">⚠ {error}</div>
  if (!rep) return null

  const arStatusChip = (s: string) =>
    s === 'paid' ? <span className="chip ok">✓ Paid</span>
      : s === 'overdue' ? <span className="chip bad">Overdue</span>
      : s === 'draft' ? <span className="chip">Draft</span>
      : <span className="chip info">In Progress</span>

  return (
    <div className="rep-body">
      <div className="rep-stats">
        <Stat label="Total Contracts" value={String(rep.stats.total)} note="Live deals" />
        <Stat label="Signed (Binding)" value={String(rep.stats.signed)} note={rep.stats.total ? `${Math.round((rep.stats.signed / rep.stats.total) * 100)}%` : '—'} />
        <Stat label="PPSR Lodged" value={String(rep.stats.ppsrLodged)} note="With lodgement ref" />
        <Stat label="Pending Signature" value={String(rep.stats.pending)} note="Follow-up needed" />
      </div>

      <div className="rep-card">
        <div className="rep-card-h">
          <h3>Vehicle Purchase Agreement Register (Signed Contracts)</h3>
          <button className="rep-btn" onClick={exportBos}>⭳ Export</button>
        </div>
        <div className="rep-table-wrap">
          <table>
            <thead>
              <tr><th>Deal ID</th><th>Vehicle</th><th>VIN</th><th>Buyer</th><th>Election Path</th><th>Signed</th><th>PPSR Ref</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rep.bosRegister.length === 0 ? (
                <tr><td colSpan={8} className="rep-none">No signed contracts yet.</td></tr>
              ) : rep.bosRegister.map((r) => (
                <tr key={r.dealId}>
                  <td><b>{shortId(r.dealId)}</b></td>
                  <td>{r.vehicle}</td>
                  <td className="mono">{r.vin || '—'}</td>
                  <td>{r.buyer}</td>
                  <td><span className="chip info">{r.electionLabel}</span></td>
                  <td className="mono">{isoDate(r.signedAt)}</td>
                  <td className="mono">{r.ppsrRef || '—'}</td>
                  <td>{r.complete ? <span className="chip ok">✓ Complete</span> : <span className="chip warn">Incomplete</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>AR Contract Register (Account Receivable Terms)</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead>
              <tr><th>Deal ID</th><th>Vehicle</th><th>Buyer</th><th>Contract Date</th><th>Terms</th><th>Invoice Issued</th><th>Due Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rep.arRegister.length === 0 ? (
                <tr><td colSpan={8} className="rep-none">No invoices issued yet.</td></tr>
              ) : rep.arRegister.map((r, i) => (
                <tr key={r.dealId + i}>
                  <td><b>{shortId(r.dealId)}</b></td>
                  <td>{r.vehicle}</td>
                  <td>{r.buyer}</td>
                  <td className="mono">{isoDate(r.contractDate)}</td>
                  <td>{r.termsDays != null ? `${r.termsDays} Days` : '—'}</td>
                  <td className="mono">{isoDate(r.invoiceIssued)}</td>
                  <td className="mono">{isoDate(r.dueDate)}</td>
                  <td>{arStatusChip(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Pending Signatures — Follow-Up Required</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead>
              <tr><th>Deal ID</th><th>Vehicle</th><th>Buyer</th><th>BoS Sent</th><th>Days Pending</th></tr>
            </thead>
            <tbody>
              {rep.pending.length === 0 ? (
                <tr><td colSpan={5} className="rep-none">Nothing awaiting signature.</td></tr>
              ) : rep.pending.map((r) => (
                <tr key={r.dealId}>
                  <td><b>{shortId(r.dealId)}</b></td>
                  <td>{r.vehicle}</td>
                  <td>{r.buyer}</td>
                  <td className="mono">{fmtDate(r.createdAt)}</td>
                  <td><span className={`chip ${r.daysPending >= 3 ? 'bad' : 'warn'}`}>{r.daysPending} day{r.daysPending === 1 ? '' : 's'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ==========================================================================
// MONTH-END TAB
// ==========================================================================

function MonthEndReportTab() {
  /*
   * Eighteen months, not six.
   *
   * Winnie, 1 Sep: "Closing stock in Mar26 and Jun26 isn't correct." With six
   * months on the picker and today in September, March 2026 was not on the list
   * at all - she could not open the month she was asking about without knowing
   * to reach for Custom range. Eighteen covers a full financial year plus the
   * comparative period, which is the span a month-end close is actually
   * reviewed over.
   */
  const months = useMemo(() => recentMonths(18), [])
  const [monthKey, setMonthKey] = useState(months[0].key)
  // Winnie, v4: a close is not always a calendar month. Picking "Custom range"
  // opens two dates and the report is cut to exactly those days, `to` included.
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const custom = monthKey === 'custom'
  const [salespersonId, setSalespersonId] = useState<string>('')
  const [rep, setRep] = useState<MonthEndReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [journal, setJournal] = useState<ClosingStockJournal | null>(null)
  const [journalBusy, setJournalBusy] = useState(false)
  const [journalNote, setJournalNote] = useState<string | null>(null)

  // §6.2 asks for the closing-stock journal to auto-reverse on the 1st. There
  // is no scheduler here, so anything already past its reversal date is backed
  // out when this screen loads — a catch-up rather than a cron, but the ledger
  // is never left showing an entry that should have gone weeks ago.
  useEffect(() => {
    reverseDueJournals().catch(() => {})
  }, [])

  useEffect(() => {
    getJournal(`${monthKey}-01`).then(setJournal).catch(() => setJournal(null))
  }, [monthKey, journalNote])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadMonthEndReport(
      monthKey,
      salespersonId || null,
      custom ? { from: fromDate || null, to: toDate || null } : null,
    )
      .then((r) => !cancelled && setRep(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [monthKey, salespersonId, custom, fromDate, toDate])

  const postJournalNow = async () => {
    if (!rep) return
    setJournalBusy(true)
    setJournalNote(null)
    try {
      const res = await postJournal({
        periodMonth: rep.periodMonth,
        amount: rep.journal.amount,
        vehicleCount: rep.journal.vehicleCount,
        reverseOn: rep.journal.reverseOn,
      })
      setJournalNote(res.message)
    } catch (e) {
      setJournalNote(e instanceof Error ? e.message : 'Could not post the journal')
    } finally {
      setJournalBusy(false)
    }
  }

  const pushJournalNow = async () => {
    if (!journal) return
    setJournalBusy(true)
    try {
      const res = await pushJournalToXero(journal)
      setJournalNote(res.message)
    } finally {
      setJournalBusy(false)
    }
  }

  const reverseNow = async () => {
    if (!journal) return
    setJournalBusy(true)
    try {
      const res = await reverseJournal(journal.id)
      setJournalNote(res.message)
    } finally {
      setJournalBusy(false)
    }
  }

  const exportStock = () => {
    if (!rep) return
    const csv = toCsv(
      ['Vehicle', 'Stock No', 'Date Acquired', 'Days on Hand', 'Age Bucket', 'Salesperson', 'Buy-In (incl GST)', 'Capture', 'GST Claimable', 'Landed Cost', 'GST Treatment'],
      rep.stocktake.map((r) => [
        r.vehicle, r.stockNumber, isoDate(r.acquiredAt), String(r.daysOnHand), AGE_BUCKET_LABEL[r.ageBucket],
        r.salesperson, r.buyIn.toFixed(2), r.capitalised.toFixed(2),
        r.gstClaimable.toFixed(2), r.landedCost.toFixed(2), r.gstTreatment,
      ])
    )
    downloadCsv(`eom-stocktake-${monthKey}.csv`, csv)
  }

  return (
    <div className="rep-body">
      <div className="rep-toolbar">
        <label className="rep-month-label">Period</label>
        <select className="rep-select" value={monthKey} onChange={(e) => setMonthKey(e.target.value)}>
          {months.map((m) => (<option key={m.key} value={m.key}>{m.label}</option>))}
        <option value="custom">Custom range…</option>
        </select>
        {custom && (
          <>
            <label className="rep-month-label">From</label>
            <input
              className="rep-select"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <label className="rep-month-label">To</label>
            <input
              className="rep-select"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </>
        )}
        <label className="rep-month-label">Salesperson</label>
        <select
          className="rep-select"
          value={salespersonId}
          onChange={(e) => setSalespersonId(e.target.value)}
        >
          {/* Winnie, 15 Sep: the universal "everything/everyone included" option is always "All" —
              never "Everyone", "Any Role" or "Any Status". This dropdown is the one she named. */}
          <option value="">All</option>
          {(rep?.salespeople || []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      </div>

      {loading ? <div className="rep-loading">Loading month-end…</div>
        : error ? <div className="rep-error">⚠ {error}</div>
        : !rep ? null : (
          <>
            <div className="rep-stats">
              <Stat label="Vehicles on Hand (EOM)" value={String(rep.stats.onHand)} note={rep.periodLabel} />
              <Stat label="Total Landed Cost" value={money(rep.stats.landedCost)} note="Closing stock value" />
              <Stat label="Notional GST (ITC)" value={money(rep.stats.notionalGst)} note="claimable per §4.4 treatment" />
              <Stat label="Xero Journal (DR 631)" value={money(rep.journal.amount)} note={`Reverses ${rep.journal.reverseDate}`} />
            </div>

            {/* Winnie 1 Sep: the closing figure now shows its working, so it can be
                reconciled against EasyCars line by line rather than taken on trust. */}
            <div className="rep-card">
              <div className="rep-card-head">
                <h3>Stock roll-forward &mdash; how the closing figure is built</h3>
              </div>
              <div className="rep-table-wrap">
                <table className="rollfwd">
                  <thead>
                    <tr>
                      <th>Movement</th>
                      <th className="num">Vehicles</th>
                      <th className="num">Landed cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Opening stock</td>
                      <td className="num">{rep.rollForward.openingVehicles}</td>
                      <td className="num">{money(rep.rollForward.openingStock)}</td>
                    </tr>
                    <tr>
                      <td>Add: net purchases (buy-in, net of GST claimable)</td>
                      <td className="num">{rep.rollForward.purchasedVehicles}</td>
                      <td className="num">{money(rep.rollForward.netPurchases)}</td>
                    </tr>
                    <tr>
                      <td>Add: direct reconditioning costs</td>
                      <td className="num">&mdash;</td>
                      <td className="num">{money(rep.rollForward.reconditioning)}</td>
                    </tr>
                    <tr>
                      <td>Less: cost of goods sold</td>
                      <td className="num">{rep.rollForward.soldVehicles}</td>
                      <td className="num">({money(rep.rollForward.costOfGoodsSold)})</td>
                    </tr>
                    {Math.abs(rep.rollForward.variance) >= 0.01 && (
                      <tr className="rf-variance">
                        <td>Unreconciled variance</td>
                        <td className="num">&mdash;</td>
                        <td className="num">{money(rep.rollForward.variance)}</td>
                      </tr>
                    )}
                    <tr className="rf-total">
                      <td>Closing stock</td>
                      <td className="num">{rep.rollForward.closingVehicles}</td>
                      <td className="num">{money(rep.rollForward.closingStock)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="rep-note">
                <strong>Closing stock = opening stock + net purchases + direct
                reconditioning costs &minus; cost of goods sold.</strong>{' '}
                Everything here is on the same landed-cost basis as the stocktake below.
                Reconditioning carries no date of its own, so it is attributed to the month
                the vehicle was acquired. Any variance is shown rather than absorbed: it is a
                vehicle counted as a purchase that is in neither closing stock nor cost of
                goods sold &mdash; typically one acquired but not yet paid for.
              </p>
            </div>
            <div className="rep-card">
              <div className="rep-card-head">
                <h3>Ageing — vehicles on hand by days in stock</h3>
              </div>
              <div className="rep-ageing">
                {rep.ageing.map((a) => (
                  <div key={a.bucket} className={`rep-age-cell${a.bucket === '30_plus' && a.vehicles > 0 ? ' hot' : ''}`}>
                    <div className="rep-age-label">{a.label}</div>
                    <div className="rep-age-count">{a.vehicles}</div>
                    <div className="rep-age-value">{money(a.landedCost)} landed</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rep-card">
              <div className="rep-card-h">
                <h3>EOM Stocktake — Vehicles on Hand (Landed Cost)</h3>
                <button className="rep-btn" onClick={exportStock}>⭳ Export</button>
              </div>
              <div className="rep-table-wrap">
                <table>
                  <thead>
                    <tr><th>Vehicle</th><th>Stock No</th><th>Acquired</th><th className="num">Days</th><th>Salesperson</th><th className="num">Buy-In (incl GST)</th><th className="num">Capture</th><th className="num">GST Claimable</th><th className="num">Landed Cost</th><th>GST Treatment</th></tr>
                  </thead>
                  <tbody>
                    {rep.stocktake.length === 0 ? (
                      <tr><td colSpan={10} className="rep-none">No vehicles on hand.</td></tr>
                    ) : rep.stocktake.map((r) => (
                      <tr key={r.dealId}>
                        <td><b>{r.vehicle}</b></td>
                        <td className="mono">{r.stockNumber || '—'}</td>
                        <td className="mono">{isoDate(r.acquiredAt)}</td>
                        <td className={`num${r.ageBucket === '30_plus' ? ' rep-bad' : ''}`}>{r.daysOnHand}</td>
                        <td>{r.salesperson}</td>
                        <td className="num">{money(r.buyIn)}</td>
                        <td className="num">{money(r.capitalised)}</td>
                        <td className="num">{money(r.gstClaimable)}</td>
                        <td className="num">{money(r.landedCost)}</td>
                        <td><span className={`chip ${r.hasTaxInvoice ? 'ok' : 'info'}`}>{r.gstTreatment}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rep.stocktake.length > 0 && (
                <div className="rep-totals">
                  <div><strong>Total Buy-In:</strong> {money(rep.totals.buyIn)}</div>
                  <div><strong>Total Capture:</strong> {money(rep.totals.capitalised)}</div>
                  <div><strong>Total Landed Cost:</strong> {money(rep.totals.landedCost)}</div>
                </div>
              )}
              {rep.totals.byTaxType.length > 0 && (
                <div className="rep-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Split by tax type</th>
                        <th className="num">Vehicles</th>
                        <th className="num">Buy-In (incl GST)</th>
                        <th className="num">Capture</th>
                        <th className="num">GST Claimable</th>
                        <th className="num">Landed Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rep.totals.byTaxType.map((t) => (
                        <tr key={t.treatment}>
                          <td>{t.treatment}</td>
                          <td className="num">{t.vehicles}</td>
                          <td className="num">{money(t.buyIn)}</td>
                          <td className="num">{money(t.capitalised)}</td>
                          <td className="num">{money(t.gstClaimable)}</td>
                          <td className="num">{money(t.landedCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rep-card">
              <div className="rep-card-head">
                <h3>Closing-Stock Journal</h3>
                <div className="rep-filters">
                  <span className={`rep-chip ${journal?.status === 'posted' ? 'credit-ok' : journal?.status === 'reversed' ? '' : 'credit-suspended'}`}>
                    {journal ? journal.status : 'not posted'}
                  </span>
                  {(!journal || journal.status === 'draft') && (
                    <button className="rep-btn primary" onClick={postJournalNow} disabled={journalBusy || custom} title="Posting is monthly">
                      Post journal
                    </button>
                  )}
                  {journal?.status === 'posted' && !journal.pushed_at && (
                    <button className="rep-btn" onClick={pushJournalNow} disabled={journalBusy}>
                      Push to Xero
                    </button>
                  )}
                  {journal?.status === 'posted' && (
                    <button className="rep-btn" onClick={reverseNow} disabled={journalBusy}>
                      Reverse now
                    </button>
                  )}
                </div>
              </div>
              <div className="rep-note">
                ⓘ Flagged BAS-excluded — the entry has no GST effect on either side. It reverses on{' '}
                {rep.journal.reverseDate}; anything already past its reversal date is backed out when
                this screen loads.
                {journal?.xero_journal_id && (
                  <> Posted to Xero as <strong>{journal.xero_journal_id}</strong>.</>
                )}
              </div>
              {journalNote && <div className="rep-alert ok">{journalNote}</div>}
              <div className="rep-table-wrap">
                <table className="journal">
                  <thead>
                    <tr><th>Account</th><th>Code</th><th className="num">Debit</th><th className="num">Credit</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><b>Vehicle Inventory</b></td><td className="mono">{rep.journal.debitAccount}</td><td className="num">{money(rep.journal.amount)}</td><td className="num">—</td><td>EOM Stocktake — Movement in Stock</td></tr>
                    <tr><td><b>Cost of Goods Sold</b></td><td className="mono">{rep.journal.creditAccount}</td><td className="num">—</td><td className="num">{money(rep.journal.amount)}</td><td>EOM Stocktake — Movement in Stock</td></tr>
                    <tr className="jtotal"><td colSpan={2}>Total</td><td className="num">{money(rep.journal.amount)}</td><td className="num">{money(rep.journal.amount)}</td><td></td></tr>
                  </tbody>
                </table>
              </div>
              <div className="rep-note reverse">
                <strong>Reversal Entry ({rep.journal.reverseDate}):</strong> DR COGS {rep.journal.creditAccount} {money(rep.journal.amount)} · CR Vehicle Inventory {rep.journal.debitAccount} {money(rep.journal.amount)}
              </div>
            </div>

            <div className="rep-card">
              <div className="rep-card-h"><h3>Acquisition Report — Raw Seller Spend ({rep.periodLabel})</h3></div>
              <div className="rep-table-wrap">
                <table>
                  <thead>
                    <tr><th>Deal ID</th><th>Vehicle</th><th>Seller</th><th>Method</th><th>Tax type</th><th className="num">Purchase (incl GST)</th><th className="num">Transport</th><th className="num">Repairs</th><th className="num">Total Acquisition</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {rep.acquisition.length === 0 ? (
                      <tr><td colSpan={10} className="rep-none">No acquisitions in this period.</td></tr>
                    ) : rep.acquisition.map((r) => (
                      <tr key={r.dealId}>
                        <td><b>{shortId(r.dealId)}</b></td>
                        <td>{r.vehicle}</td>
                        <td>{r.seller}</td>
                        <td>{r.method}</td>
                        <td>{r.taxType}</td>
                        <td className="num">{money(r.purchasePrice)}</td>
                        <td className="num">{money(r.capitalisedTransport)}</td>
                        <td className="num">{money(r.capitalisedRepairs)}</td>
                        <td className="num">{money(r.totalCost)}</td>
                        <td><span className="chip info">{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rep.acquisition.length > 0 && (
                <div className="rep-totals">
                  <div><strong>Total Raw Seller Spend:</strong> {money(rep.acquisitionTotals.purchaseOnly)}</div>
                  <div><strong>Vehicles Acquired:</strong> {rep.acquisitionTotals.count}</div>
                  <div className="rep-note" style={{ padding: 0 }}>
                    Purchase amounts only, per §6.3 — transport and repairs are shown per vehicle but
                    excluded from the spend total.
                  </div>
                </div>
              )}
            </div>

            <div className="rep-card">
              <div className="rep-card-head"><h3>Spend by supplier</h3></div>
              <div className="rep-table-wrap">
                <table>
                  <thead>
                    <tr><th>Supplier</th><th className="num">Vehicles</th><th className="num">Spend</th><th className="num">Share</th></tr>
                  </thead>
                  <tbody>
                    {rep.supplierSpend.length === 0 ? (
                      <tr><td colSpan={4} className="rep-none">No spend in this period.</td></tr>
                    ) : rep.supplierSpend.map((sp) => (
                      <tr key={sp.supplier}>
                        <td><b>{sp.supplier}</b></td>
                        <td className="num">{sp.vehicles}</td>
                        <td className="num">{money(sp.spend)}</td>
                        <td className="num">
                          {rep.acquisitionTotals.purchaseOnly
                            ? `${Math.round((sp.spend / rep.acquisitionTotals.purchaseOnly) * 100)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
    </div>
  )
}

// ==========================================================================
// SALES PERFORMANCE TAB
// ==========================================================================

function SalesPerformanceTab() {
  // The MTD / QTD / YTD chips stay — they are the quick answer people want most
  // days. The range sits alongside and wins when it is set, so the report can
  // answer both "how are we going this quarter" and "what did 3–17 June look
  // like" without two different controls meaning two different things.
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  return (
    <>
      <ReportRangeFilter value={range} onChange={setRange} />
      <SalesPerformanceBody key={`${range.from}|${range.to}`} range={range} />
    </>
  )
}

function SalesPerformanceBody({ range }: { range: DateRange }) {
  const [period, setPeriod] = useState<KpiPeriod>('month')
  const [rep, setRep] = useState<SalesPerformanceReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadSalesPerformance(period, range)
      .then((r) => !cancelled && setRep(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [period])

  if (loading) return <div className="rep-loading">Loading sales performance…</div>
  if (error) return <div className="rep-error">⚠ {error}</div>
  if (!rep) return null
  if (rep.members.length === 0) return <EmptyModel title="Sales Performance" />

  const roleName = (r: string) => (r === 'buyer_sourcer' ? 'Buyer-Sourcer' : r === 'sales' ? 'Sales' : 'Spotter')
  const pct = (n: number) => `${n.toFixed(1)}%`
  /* A rate with an empty denominator is not zero. "—" says it was not measured. */
  const rate = (n: number | null) => (n === null ? '—' : `${n}%`)
  const days = (n: number | null) => (n === null ? '—' : `${n.toFixed(1)}d`)

  return (
    <div className="rep-body">
      <div className="rep-toolbar">
        <label className="rep-month-label">Period</label>
        <select className="rep-select" value={period} onChange={(e) => setPeriod(e.target.value as KpiPeriod)}>
          {(['day', 'week', 'month', 'quarter', 'year', 'all'] as KpiPeriod[]).map((p) => (
            <option key={p} value={p}>{KPI_PERIOD_LABEL[p]}</option>
          ))}
        </select>
      </div>

      <div className="rep-stats">
        <Stat label="Deals Closed" value={String(rep.stats.dealsClosed)} note="Realized deals" />
        <Stat label="Gross Resale" value={money(rep.stats.grossResale)} note="Across team" />
        <Stat label="Net Profit" value={money(rep.stats.grossProfit)} note="After transport, reconditioning and the buying fee" />
        <Stat label="Net Commission" value={money(rep.stats.commission)} note="Net profit × rate, losses shared" />
        <Stat
          label="Sourced→Sold %"
          value={rep.stats.sourcedToSold === null ? '—' : `${rep.stats.sourcedToSold}%`}
          note="Of what the team sourced this period, how much has sold"
        />
      </div>

      {/* Winnie, 13 Sep: "Update the Team summary report to the above team summary layout."
          Her layout groups the measures by the question they answer rather than listing
          twelve columns across one table nobody can read: how many deals convert, where
          they get stuck, how good they are, and what they pay. Two of the measures are new
          on that layout - average transit and the seller-type mix - and both are included
          in the group they belong to rather than bolted on the end. */}
      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Conversion — {KPI_PERIOD_LABEL[period]}</h3>
        </div>
        <p className="rep-note">
          Each stage counts on the date the deal reached it, so a vehicle sourced in June does not
          keep reappearing in July's offers. A rate with nothing under it reads “—”: nobody made an
          offer is a different fact from every offer being refused.
        </p>
        <div className="rep-table-wrap">
          <table className="rep-table">
            <thead>
              <tr>
                <th>Team member</th>
                <th className="num">Opps</th>
                <th className="num">Offers</th>
                <th className="num">Offer rate</th>
                <th className="num">Accept %</th>
                <th className="num">Acc→paid</th>
                <th className="num">Sourced→sold</th>
              </tr>
            </thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b></td>
                  <td className="num">{m.kpis.opportunitiesCreated}</td>
                  <td className="num">{m.kpis.offersMade}</td>
                  <td className="num">{rate(m.kpis.offerRate)}</td>
                  <td className="num">{m.kpis.offerAcceptanceRate === null ? '—' : `${m.kpis.offerAcceptanceRate}%`}</td>
                  <td className="num">{rate(m.kpis.accToPaidRate)}</td>
                  <td className="num">{rate(m.kpis.sourcedToSoldRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Speed &amp; bottlenecks</h3>
        </div>
        <p className="rep-note">
          Average transit is measured from a freight job opening to the vehicle being delivered, and
          only where Direct Auto is carrying it — a buyer collecting their own car has no transit
          period to measure, so those deals are outside the figure rather than counted as instant.
        </p>
        <div className="rep-table-wrap">
          <table className="rep-table">
            <thead>
              <tr>
                <th>Team member</th>
                <th className="num">Lead→paid</th>
                <th className="num">Paid→sold</th>
                <th className="num">Avg transit</th>
                <th className="num">Awt docs</th>
                <th className="num">Awt pay</th>
                <th className="num">Awt transport</th>
              </tr>
            </thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b></td>
                  <td className="num">{days(m.kpis.avgDaysLeadToPayment)}</td>
                  <td className="num">{days(m.kpis.avgDaysPaymentToReceived)}</td>
                  <td
                    className="num"
                    title={
                      m.kpis.avgTransitDays === null
                        ? 'No freight job on this person’s deals has been marked Delivered in this period.'
                        : `Across ${m.kpis.transitDeals} delivered freight job${m.kpis.transitDeals === 1 ? '' : 's'}.`
                    }
                  >
                    {days(m.kpis.avgTransitDays)}
                  </td>
                  <td className="num">{m.kpis.awaitingDocuments}</td>
                  <td className="num">{m.kpis.awaitingPayment}</td>
                  <td className="num">{m.kpis.awaitingTransport}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Deal quality</h3>
        </div>
        <p className="rep-note">
          The seller mix is not a rate to optimise — it is the context that makes every other rate on
          these tables comparable between people. A Type 3–5 deal (licensed dealer, auction, tender)
          skips the purchase agreement, the inspection, the signature and the DD documents, so a
          person working mostly those deals will show faster, cleaner numbers than someone doing
          private-seller work.
        </p>
        <div className="rep-table-wrap">
          <table className="rep-table">
            <thead>
              <tr>
                <th>Team member</th>
                <th className="num">Avg margin</th>
                <th className="num">Margin %</th>
                <th className="num">Below B/E</th>
                <th className="num">Type 1/2 mix</th>
                <th className="num">Type 3/4/5 mix</th>
              </tr>
            </thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b></td>
                  <td className="num">{money(m.kpis.avgPurchaseMargin)}</td>
                  <td className="num">{pct(m.marginPct)}</td>
                  <td className="num">{m.belowBreakEven}</td>
                  <td
                    className="num"
                    title={m.kpis.sellerTypeMix.deals ? `${m.kpis.sellerTypeMix.type1}% private individual, ${m.kpis.sellerTypeMix.type2}% private company` : 'No deals sourced in this period.'}
                  >
                    {m.kpis.sellerTypeMix.deals ? `${m.kpis.sellerTypeMix.type1 + m.kpis.sellerTypeMix.type2}%` : '—'}
                  </td>
                  <td
                    className="num"
                    title={m.kpis.sellerTypeMix.deals ? 'Licensed dealer, auction and tender, which share the fast-track workflow.' : 'No deals sourced in this period.'}
                  >
                    {m.kpis.sellerTypeMix.deals ? `${m.kpis.sellerTypeMix.type345}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Commission</h3>
        </div>
        <div className="rep-table-wrap">
          <table className="rep-table">
            <thead>
              <tr>
                <th>Team member</th>
                <th className="num">Deals</th>
                <th className="num">Gross resale</th>
                <th className="num">Net profit</th>
                <th className="num">Margin %</th>
                <th className="num">Commission</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b> <span className="rep-muted">({roleName(m.role)})</span></td>
                  <td className="num">{m.dealsClosed}</td>
                  <td className="num">{money(m.grossResale)}</td>
                  <td className="num">{money(m.grossProfit)}</td>
                  <td className="num">{pct(m.marginPct)}</td>
                  <td className="num">
                    {money(m.commission)} <span className="rep-muted">({m.termsLabel})</span>
                  </td>
                  <td>{m.lossCount > 0 ? <span className="chip warn">⚡ {m.lossCount} loss deal{m.lossCount === 1 ? '' : 's'}</span> : <span className="chip ok">✓ Active</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-grid2">
        <div className="rep-card">
          <div className="rep-card-h"><h3>AR Cycle</h3></div>
          <div className="rep-table-wrap">
            <table>
              <thead><tr><th>Team Member</th><th className="num">Avg Days (Issued→Paid)</th></tr></thead>
              <tbody>
                {rep.members.map((m) => (
                  <tr key={m.memberId}><td><b>{m.name}</b></td><td className="num">{m.arAvgDays != null ? `${m.arAvgDays.toFixed(1)} days` : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="rep-card">
          <div className="rep-card-h"><h3>Path Mix</h3></div>
          <div className="rep-table-wrap">
            <table>
              <thead><tr><th>Team Member</th><th>P1 (D+L)</th><th>P2 (D)</th><th>P3 (∅)</th></tr></thead>
              <tbody>
                {rep.members.map((m) => (
                  <tr key={m.memberId}>
                    <td><b>{m.name}</b></td>
                    <td><span className="chip info">{m.pathMix.path1}%</span></td>
                    <td><span className="chip info">{m.pathMix.path2}%</span></td>
                    <td><span className="chip info">{m.pathMix.path3}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>AR Terms Deviation</h3></div>
        <div className="rep-note">ⓘ Invoices issued on terms weaker than the member's risk-rating recommendation.</div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Team Member</th><th>Risk Rating</th><th className="num">Invoices</th><th className="num">Weaker (Count)</th><th className="num">Weaker (Value)</th><th>Status</th></tr></thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b></td>
                  <td>{m.riskDays} days</td>
                  <td className="num">{m.arInvoices}</td>
                  <td className="num">{m.weakerTermsCount}</td>
                  <td className="num">{money(m.weakerTermsValue)}</td>
                  <td>{m.weakerTermsCount > 0 ? <span className="chip warn">⚠ Review</span> : <span className="chip ok">✓ Compliant</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Loss Deals</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Team Member</th><th className="num">Count</th><th className="num">Total Loss</th><th>Trend</th></tr></thead>
            <tbody>
              {rep.members.map((m) => (
                <tr key={m.memberId}>
                  <td><b>{m.name}</b></td>
                  <td className="num">{m.lossCount}</td>
                  <td className="num">{money(m.lossValue)}</td>
                  <td>{m.lossCount === 0 ? <span className="chip ok">✓ None</span> : <span className="chip warn">Monitor</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ==========================================================================
// COMMISSION RECON TAB
// ==========================================================================

/**
 * The range lives in a wrapper and the body is remounted when it changes, so the
 * report refetches without the range having to be threaded through the body's own
 * effect. Same pattern for every report that gained a range.
 */
function CommissionReconTab() {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  return (
    <>
      <ReportRangeFilter value={range} onChange={setRange} />
      <CommissionReconBody key={`${range.from}|${range.to}`} range={range} />
    </>
  )
}

function CommissionReconBody({ range }: { range: DateRange }) {
  const [rep, setRep] = useState<CommissionReconReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadCommissionRecon(range)
      .then((r) => !cancelled && setRep(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [])

  if (loading) return <div className="rep-loading">Loading commission recon…</div>
  if (error) return <div className="rep-error">⚠ {error}</div>
  if (!rep) return null
  if (rep.summary.length === 0) return <EmptyModel title="Commission Reconciliation" />

  const summaryChip = (s: string) =>
    s === 'matched' ? <span className="chip ok">✓ Matched</span>
      : s === 'overcharge' ? <span className="chip warn">⚠ Overcharge</span>
      : s === 'variance' ? <span className="chip warn">⚠ Variance</span>
      : <span className="chip bad">Held</span>

  return (
    <div className="rep-body">
      <div className="rep-stats">
        <Stat label="Total Entitlement (ex GST)" value={money(rep.stats.calculated)} note="Share of net margin" />
        <Stat label="GST on Commission" value={money(rep.stats.gst)} note="Added on the end" />
        <Stat label="Total Payable (incl GST)" value={money(rep.stats.payable)} />
        <Stat label="Matched (Cleared)" value={money(rep.stats.matched)} note="Ready for payment" />
        <Stat label="Held — Variance" value={money(rep.stats.heldVariance)} note={`${rep.exceptions.length} exception${rep.exceptions.length === 1 ? '' : 's'}`} />
        <Stat label="Held — Dup/Unmatched" value={money(rep.stats.heldOther)} note={`${rep.duplicates.length + rep.unmatched.length} item(s)`} />
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Reconciliation Summary by Payee</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Payee</th><th className="num">Commission (ex GST)</th><th className="num">GST</th><th className="num">Payable</th><th className="num">Invoiced</th><th className="num">Variance</th><th>Status</th></tr></thead>
            <tbody>
              {rep.summary.map((s) => (
                <tr key={s.memberId}>
                  <td><b>{s.payee}</b> <span className="rep-muted">({s.role === 'buyer_sourcer' ? 'Buyer' : 'Spotter'})</span></td>
                  <td className="num">{money(s.calculated)}</td><td className="num">{money(s.gst)}</td><td className="num">{money(s.payable)}</td>
                  <td className="num">{money(s.invoiced)}</td>
                  <td className="num">{money(s.variance)}</td>
                  <td>{summaryChip(s.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Matched Items — Ready for Payment</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Deal ID</th><th>Vehicle</th><th>Payee</th><th>Fee Type</th><th className="num">Commission (ex GST)</th><th className="num">GST</th><th className="num">Payable</th><th className="num">Invoiced</th><th>Invoice Ref</th></tr></thead>
            <tbody>
              {rep.matched.length === 0 ? <tr><td colSpan={9} className="rep-none">No matched items.</td></tr>
                : rep.matched.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.dealId ? shortId(r.dealId) : '—'}</b></td>
                    <td>{r.vehicle}</td>
                    <td>{r.payee}</td>
                    <td><span className="chip info">{r.feeType}</span></td>
                    <td className="num">{money(r.calculated)}</td><td className="num">{money(r.gst)}</td><td className="num">{money(r.payable)}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td className="mono">{r.invoiceRef}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Overcharge / Variance Exceptions — Held</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Deal ID</th><th>Payee</th><th className="num">Commission (ex GST)</th><th className="num">GST</th><th className="num">Payable</th><th className="num">Invoiced</th><th className="num">Variance</th><th>Action</th></tr></thead>
            <tbody>
              {rep.exceptions.length === 0 ? <tr><td colSpan={6} className="rep-none">No exceptions.</td></tr>
                : rep.exceptions.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.dealId ? shortId(r.dealId) : '—'}</b></td>
                    <td>{r.payee}</td>
                    <td className="num">{money(r.calculated)}</td><td className="num">{money(r.gst)}</td><td className="num">{money(r.payable)}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td className="num">{money(r.variance)}</td>
                    <td>{r.outcome === 'overcharge' ? 'Query invoice; hold until clarified' : 'Query difference with payee'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Duplicate Detection — Never Released (Byte-Match)</h3></div>
        <div className="rep-note">ⓘ Duplicates detected via fingerprint / repeat invoice. Prevents double payment.</div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Invoice Ref</th><th>Payee</th><th className="num">Amount</th><th>Matches</th><th>Status</th></tr></thead>
            <tbody>
              {rep.duplicates.length === 0 ? <tr><td colSpan={5} className="rep-none">No duplicates detected.</td></tr>
                : rep.duplicates.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.invoiceRef}</b></td>
                    <td>{r.payee}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td>{r.matchesRef ? <><b>{r.matchesRef}</b> (identical)</> : 'Repeat invoice'}</td>
                    <td><span className="chip bad">✗ Duplicate — Blocked</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h"><h3>Unmatched Items — Held Pending Review</h3></div>
        <div className="rep-table-wrap">
          <table>
            <thead><tr><th>Invoice Ref</th><th>Payee</th><th className="num">Amount</th><th>Issue</th></tr></thead>
            <tbody>
              {rep.unmatched.length === 0 ? <tr><td colSpan={4} className="rep-none">Nothing unmatched.</td></tr>
                : rep.unmatched.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.invoiceRef}</b></td>
                    <td>{r.payee}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td>No matching deal attribution / entitlement</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/** DA-style short id from a UUID for compact display. */
function shortId(id: string) {
  return id.length > 8 ? id.slice(0, 8).toUpperCase() : id
}

// ==========================================================================
// P&L
// ==========================================================================

const isoDay = (d: Date) => d.toISOString().slice(0, 10)

function ProfitAndLossTab() {
  const today = new Date()
  const [from, setFrom] = useState(isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))))
  const [to, setTo] = useState(isoDay(today))
  const [openAccount, setOpenAccount] = useState<string | null>(null)
  const [pl, setPl] = useState<ProfitAndLoss | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadProfitAndLoss(from, to, `${fmtDate(from)} – ${fmtDate(to)}`)
      .then((r) => { if (!cancelled) setPl(r) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [from, to])

  /*
   * Winnie asked the P&L to open down to the itemised transactions behind an
   * account. The rows travel with the line out of plService, so what appears
   * underneath a figure is the same set that produced it, not a second query
   * that could quietly disagree with the number above it.
   */
  const row = (l: PlLine) => {
    const detail = l.rows || []
    const open = openAccount === l.account
    return (
      <Fragment key={l.account}>
        <tr>
          <td>
            {detail.length ? (
              <button
                type="button"
                className="rep-pl-toggle"
                onClick={() => setOpenAccount(open ? null : l.account)}
                aria-expanded={open}
              >
                <span aria-hidden="true">{open ? '▾' : '▸'}</span> {l.account}
              </button>
            ) : (
              l.account
            )}
            {l.note ? <div className="rep-muted">{l.note}</div> : null}
          </td>
          <td className="num">{l.vehicles == null ? '—' : l.vehicles}</td>
          <td className="num">{money(l.gross)}</td>
          <td className="num">{l.exGst == null ? '—' : money(l.exGst)}</td>
        </tr>
        {open && detail.length ? (
          <tr>
            <td colSpan={4}>
              <table className="rep-pl-detail">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Item</th>
                    <th>Reference</th>
                    <th className="num">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.map((r) => (
                    <tr key={r.id}>
                      <td>{r.when || '—'}</td>
                      <td>{r.what}</td>
                      <td>{r.reference || '—'}</td>
                      <td className="num">{money(r.gross)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="rep-muted">
                {detail.length} transaction{detail.length === 1 ? '' : 's'}, totalling{' '}
                {money(detail.reduce((s, r) => s + r.gross, 0))} — this is what the
                figure above is made of.
              </div>
            </td>
          </tr>
        ) : null}
      </Fragment>
    )
  }

  return (
    <div className="rep-body">
      <div className="rep-toolbar">
        <label className="rep-month-label">From</label>
        <input className="rep-select" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <label className="rep-month-label">To</label>
        <input className="rep-select" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {loading ? <div className="rep-loading">Loading P&amp;L…</div>
        : error ? <div className="rep-error">{error}</div>
        : !pl ? null : (
        <>
          <div className="rep-stats">
            <Stat label="Total Trading Income" value={money(pl.totalIncomeGross)} note={pl.periodLabel} />
            <Stat label="Total Cost of Sales" value={money(pl.totalCostGross)} />
            <Stat label="Gross Profit" value={money(pl.grossProfitGross)} note="Income less cost of sales" />
          </div>

          <div className="rep-card">
            <div className="rep-card-h"><h3>Trading Income</h3></div>
            <div className="rep-table-wrap">
              <table>
                <thead><tr><th>Account</th><th className="num">Vehicles</th><th className="num">Gross</th><th className="num">Ex GST</th></tr></thead>
                <tbody>{pl.tradingIncome.map(row)}</tbody>
              </table>
            </div>
          </div>

          <div className="rep-card">
            <div className="rep-card-h"><h3>Cost of Sales</h3></div>
            <div className="rep-table-wrap">
              <table>
                <thead><tr><th>Account</th><th className="num">Vehicles</th><th className="num">Gross</th><th className="num">Ex GST</th></tr></thead>
                <tbody>{pl.costOfSales.map(row)}</tbody>
              </table>
            </div>
          </div>

          <p className="rep-note">
            A sale is classified by how the vehicle was <b>acquired</b>, not how it was sold — a car bought
            on a tax invoice sells as Full GST Supply, one bought from a private seller sells under the
            margin scheme. Ex-GST is left blank on margin-scheme sales because the GST there falls on the
            margin, and which period that credit belongs to is the treatment question still open with
            Thomas Sparks. Nothing here assumes an answer to it.
          </p>
        </>
      )}
    </div>
  )
}
